import React, { FC } from 'react';
import { useFormContext } from 'react-hook-form';

import { Icon } from '@column/column-ui-kit';
import {
  Field,
  FieldContent,
  FieldDescription,
  FieldGroup,
  FieldLabel,
  RadioGroupItem,
  RadioGroup as TypeRadioGroup,
} from '@column/ui';

import { CreateTransfer } from '../Add';
import { ROUTE } from '~/app/routes';
import { FormField } from '~/components';
import { IconTransfers } from '~/elements';
import { useNavigate } from '~/lib/navigation';
import { TransferTypeID } from '~/repositories';
import { useSessionStore } from '~/stores/Session';

import { RestrictedBadge } from './RestrictedBadge';

export type CheckTransferType = Extract<TransferTypeID, 'issue-check' | 'rdc'>;

/** Where the Check Transfer tab opens: Issue Check, unless the user can only deposit. */
export const useCheckTransferRoute = (): CheckTransferType => {
  const isAuthorized = useSessionStore((s) => s.isAuthorized);
  const canIssue = isAuthorized({ permission: 'issueChecks', permissionLevel: 'write' });
  const canDeposit = isAuthorized({ permission: 'depositChecks', permissionLevel: 'write' });
  return !canIssue && canDeposit ? 'rdc' : 'issue-check';
};

interface CheckTransferOption {
  value: CheckTransferType;
  label: string;
  description: string;
  isAuthorized: boolean;
  restrictedMessage: string;
}

interface CheckTransferTypeFieldProps {
  type: CheckTransferType;
}

export const CheckTransferTypeField: FC<CheckTransferTypeFieldProps> = ({ type }) => {
  const navigate = useNavigate();
  const { clearErrors } = useFormContext<CreateTransfer>();
  const isAuthorized = useSessionStore((s) => s.isAuthorized);

  const options: CheckTransferOption[] = [
    {
      value: 'issue-check',
      label: 'Issue Check',
      description: 'Issue a check to a specific payee.',
      isAuthorized: isAuthorized({ permission: 'issueChecks', permissionLevel: 'write' }),
      restrictedMessage: 'You do not have permission to issue checks.',
    },
    {
      value: 'rdc',
      label: 'Deposit Check',
      description: 'Deposit check into an account.',
      isAuthorized: isAuthorized({ permission: 'depositChecks', permissionLevel: 'write' }),
      restrictedMessage: 'You do not have permission to deposit checks.',
    },
  ];

  const goTo = (target: CheckTransferType | 'check') => {
    clearErrors();
    navigate(`${ROUTE.TRANSFERS}/edit/${target}`, { replace: true });
  };

  return (
    <FormField<CreateTransfer>
      id="checkTransferType"
      label="Transfer Type"
      defaultValue={type}
      description="Issue a check to pay someone, or deposit a check you received."
    >
      {() => (
        <>
          <TypeRadioGroup
            value={type}
            onValueChange={(next) => {
              if (next !== type) {
                goTo(next as CheckTransferType);
              }
            }}
          >
            <FieldGroup variant="outline" orientation="responsive">
              {options.map((option) => (
                <Field
                  key={option.value}
                  orientation="horizontal"
                  className="relative"
                  data-disabled={!option.isAuthorized || undefined}
                >
                  <RadioGroupItem
                    value={option.value}
                    id={`checkTransfer-${option.value}`}
                    data-cy={`option-checkTransfer.${option.value}`}
                    disabled={!option.isAuthorized}
                  />
                  <FieldContent>
                    <FieldLabel htmlFor={`checkTransfer-${option.value}`}>{option.label}</FieldLabel>
                    <FieldDescription className="text-xs text-pretty">{option.description}</FieldDescription>
                  </FieldContent>
                  {!option.isAuthorized && <RestrictedBadge content={option.restrictedMessage} />}
                </Field>
              ))}
            </FieldGroup>
          </TypeRadioGroup>

          {type === 'rdc' && (
            <div className="mt-2 flex animate-in items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 py-1.5 pr-1.5 pl-3 duration-200 fade-in dark:border-white/15 dark:bg-white/5">
              <IconTransfers.CheckScanner className="flex shrink-0 text-gray-600 [--icon-size:16px] dark:text-gray-300" />
              <span className="flex-1 text-xs leading-4 text-muted-foreground">
                Deposit multiple checks at once with check scanning.
              </span>
              <button
                type="button"
                onClick={() => goTo('check')}
                className="inline-flex shrink-0 cursor-pointer items-center gap-0.5 rounded-md py-1 pr-1.5 pl-2 text-xs font-medium text-muted-foreground transition-colors hover:bg-gray-150 hover:text-foreground dark:hover:bg-white/10"
              >
                Start Scanning
                <Icon.ChevronRight className="[--icon-size:14px]" />
              </button>
            </div>
          )}
        </>
      )}
    </FormField>
  );
};
